import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { logger } from "@amzn/afxtaco-utilities";
import { Project } from "@amzn/afxtaco-types";
import { Message, MessageAttachment } from "@/types";
import { flushDrawioBuffer } from "../../drawio/drawioFlushRegistry";
import {
  ExecutionState,
  isExecutionActive,
  startExecution,
} from "../../../../redux/reducers/Execution";
import { AppDispatch } from "../../../../redux/store";
import { buildPingMessage } from "../../../utils/buildPingMessage";
import { pingCustomerContext } from "../../../utils/customerContext";
import { resolveSession } from "../utils/session";
import { ChatStreamingRefs } from "./useChatStreamingRefs";
import { ChatNavState } from "./useProjectBoundaryReset";

interface WebSocketLike {
  sendMessage: (message: string) => void;
}

interface UseSendChatMessageOptions {
  projectId: string | undefined;
  ws: WebSocketLike | null | undefined;
  dispatch: AppDispatch;
  executionState: ExecutionState;
  navStateRef: React.MutableRefObject<ChatNavState>;
  refs: ChatStreamingRefs;
  messageCount: number;
  currentConversationId: string | null;
  createConversation: (projectId?: string) => string;
  addMessage: (conversationId: string, message: Omit<Message, "id">) => string;
  getProjectById: (projectId: string) => Project | undefined;
}

/** Wire shape for an attached file carried alongside the prompt in the ping. */
interface PingAttachment {
  name: string;
  s3Key: string;
}

/**
 * Returns a stable handler that sends a user message via WebSocket ping.
 *
 * The handler:
 *   1. Blocks concurrent submissions while an execution is in flight (defense
 *      in depth — `disabled` prop on ChatInput is the primary guard).
 *   2. Adds the user message and an AI placeholder to the conversation.
 *   3. Resolves the appropriate `initAction` and `sessionId` based on whether
 *      this is a new project, a follow-up to a HomePage-created session, or
 *      an existing project loaded from the API.
 *   4. Dispatches `startExecution` so the chat UI tracks the in-flight ping.
 *   5. Flushes pending drawio edits BEFORE pinging the orchestrator so the
 *      agent reads up-to-date S3 content.
 *   6. Sends the WebSocket ping.
 */
export function useSendChatMessage({
  projectId,
  ws,
  dispatch,
  executionState,
  navStateRef,
  refs,
  messageCount,
  currentConversationId,
  createConversation,
  addMessage,
  getProjectById,
}: UseSendChatMessageOptions): (
  message: string,
  attachments?: MessageAttachment[],
) => Promise<void> {
  return useCallback(
    async (message: string, attachments: MessageAttachment[] = []) => {
      if (!projectId) return;

      // Block new submissions while an execution is in flight.
      // Without this guard, a second startExecution dispatch would clear
      // streamingContent and overwrite streamingMessageIdRef, causing the
      // first request's streamed response to be lost.
      if (isExecutionActive(executionState.status)) {
        logger.warn({
          loggerMsg: "[IDEChatPanel] Blocked send — execution already active",
          currentStatus: executionState.status,
        });
        return;
      }

      // Create new conversation if none exists, associating it with this project
      let conversationId = currentConversationId;
      if (!conversationId) {
        conversationId = createConversation(projectId);
      }

      // Add user message to conversation, persisting any attachments so the
      // history can render them as read-only badges.
      addMessage(conversationId, {
        content: message,
        isUser: true,
        ...(attachments.length > 0 ? { attachments } : {}),
      });

      // Add placeholder AI message for streaming response
      const aiMessageId = addMessage(conversationId, {
        content: "",
        isUser: false,
        isLoading: true,
      });
      refs.streamingMessageIdRef.current = aiMessageId;
      refs.processedSegmentsRef.current = 0;

      // Determine session resolution
      const project = getProjectById(projectId);
      const isNewProject = Boolean(
        navStateRef.current?.isNewProject && messageCount <= 1,
      );

      const { initAction, sessionId } = resolveSession(
        isNewProject,
        refs.sessionIdRef.current ?? undefined,
        project?.latestSessionId,
        project?.latestSessionExpiresAt,
      );

      // Persist sessionId for subsequent messages
      refs.sessionIdRef.current = sessionId;

      const clientRequestId = uuidv4();

      // Track execution in Redux
      dispatch(startExecution({ projectId, sessionId, clientRequestId }));

      // Flush any pending drawio edits BEFORE pinging the orchestrator so
      // the agent sees up-to-date S3 when it reads. Done after the
      // startExecution dispatch so the chat UI's streaming placeholder is
      // tracked correctly during the flush wait.
      await flushDrawioBuffer();

      // Carry only the fields the backend needs (filename + s3Key) so the
      // agent can map each attachment to its uploaded object.
      const attachedFiles: PingAttachment[] = attachments.map((a) => ({
        name: a.name,
        s3Key: a.s3Key,
      }));

      // Customer context recorded on the project rides every turn, so the agent
      // knows which account to work with without the user restating it.
      const customerContext = pingCustomerContext(project);

      // Send WebSocket ping
      const pingMessage = await buildPingMessage({
        projectId,
        sessionId,
        clientRequestId,
        prompt: message,
        initAction,
        ...(attachedFiles.length > 0 ? { attachedFiles } : {}),
        ...(customerContext ? { customerContext } : {}),
      });
      ws?.sendMessage(pingMessage);
    },
    [
      projectId,
      ws,
      dispatch,
      executionState.status,
      navStateRef,
      refs,
      messageCount,
      currentConversationId,
      createConversation,
      addMessage,
      getProjectById,
    ],
  );
}
